import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosConfig';
import { 
    MdTrendingUp, 
    MdTrendingDown, 
    MdPeople, 
    MdVisibility, 
    MdPublic, 
    MdDevices, 
    MdRefresh, 
    MdArrowForward,
    MdSmartphone,
    MdComputer,
    MdTabletMac
} from 'react-icons/md';
import { 
    FaGoogle, 
    FaWhatsapp, 
    FaFacebook, 
    FaInstagram, 
    FaYoutube, 
    FaCompass, 
    FaShareAlt 
} from 'react-icons/fa';

const VisitorAnalyticsWidget = ({ onViewDetailed }) => {
    const [period, setPeriod] = useState('7days');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const fetchAnalytics = async (selectedPeriod = period, isSilent = false) => {
        if (!isSilent) setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`analytics/dashboard-summary/?period=${selectedPeriod}`);
            setData(res.data);
            setLastUpdated(new Date());
        } catch (err) {
            console.error("Error fetching visitor analytics:", err);
            setError("Failed to load visitor statistics. Please try again.");
        } finally {
            if (!isSilent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics(period);
    }, [period]);

    // Auto-refresh real-time data every 30 seconds silently
    useEffect(() => {
        const interval = setInterval(() => {
            fetchAnalytics(period, true);
        }, 30000);
        return () => clearInterval(interval);
    }, [period]);

    const getSourceIcon = (source) => {
        switch (source) {
            case 'Google / Organic Search': return <FaGoogle className="text-red-500 text-sm" />;
            case 'WhatsApp': return <FaWhatsapp className="text-green-500 text-sm" />;
            case 'Facebook': return <FaFacebook className="text-blue-600 text-sm" />;
            case 'Instagram': return <FaInstagram className="text-pink-600 text-sm" />;
            case 'YouTube': return <FaYoutube className="text-red-600 text-sm" />;
            case 'Direct': return <FaCompass className="text-indigo-500 text-sm" />;
            default: return <FaShareAlt className="text-gray-500 text-sm" />;
        }
    };

    const formatNumber = (num) => {
        return (num || 0).toLocaleString();
    };

    // Calculate max value for trend chart scaling
    const maxTrendVal = data?.trend?.reduce((max, item) => Math.max(max, item.visitors, item.page_views), 10) || 10;

    return (
        <div className="mt-12 border-t pt-8">
            {/* Header & Controls */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                <div>
                    <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold text-gray-800">Visitor Analytics</h3>
                        {/* Real-Time Live Pulse Badge */}
                        <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                            </span>
                            <span>Currently Online: <strong>{data?.realtime?.currently_online || 0}</strong></span>
                            <span className="text-gray-300">|</span>
                            <span className="text-emerald-600 font-normal">
                                <strong>{data?.realtime?.last_30_min_visitors || 0}</strong> in last 30 mins
                            </span>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Aggregated audience metrics & traffic breakdown • Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* Period Selector */}
                    <div className="inline-flex bg-gray-100 p-1 rounded-lg text-xs font-medium text-gray-700">
                        {[
                            { id: 'today', label: 'Today' },
                            { id: 'yesterday', label: 'Yesterday' },
                            { id: '7days', label: '7 Days' },
                            { id: '30days', label: '30 Days' },
                            { id: 'month', label: 'This Month' },
                        ].map((btn) => (
                            <button
                                key={btn.id}
                                onClick={() => setPeriod(btn.id)}
                                className={`px-3 py-1.5 rounded-md transition-all ${
                                    period === btn.id
                                        ? 'bg-white text-blue-600 shadow-sm font-bold'
                                        : 'hover:text-gray-900 text-gray-600'
                                }`}
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => fetchAnalytics(period)}
                        title="Refresh statistics"
                        className="p-2 text-gray-500 hover:text-blue-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                    >
                        <MdRefresh className={`w-4 h-4 ${loading ? 'animate-spin text-blue-600' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center justify-between">
                    <span>{error}</span>
                    <button 
                        onClick={() => fetchAnalytics(period)}
                        className="text-xs font-bold underline hover:text-red-900"
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Loading Skeleton */}
            {loading && !data && (
                <div className="space-y-6 animate-pulse">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="h-28 bg-gray-200 rounded-xl"></div>
                        ))}
                    </div>
                    <div className="h-64 bg-gray-200 rounded-2xl"></div>
                </div>
            )}

            {data && (
                <div className="space-y-6">
                    {/* 1. Summary KPI Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Visitors Today */}
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
                                <span>Visitors Today</span>
                                <MdPeople className="w-4 h-4 text-blue-500" />
                            </div>
                            <div className="text-2xl font-black text-gray-800">
                                {formatNumber(data.kpi.visitors_today.value)}
                            </div>
                            <div className="mt-2 flex items-center gap-1 text-xs font-semibold">
                                {data.kpi.visitors_today.is_increase ? (
                                    <span className="text-emerald-600 flex items-center">
                                        <MdTrendingUp className="mr-0.5" /> +{data.kpi.visitors_today.change_pct}%
                                    </span>
                                ) : (
                                    <span className="text-rose-600 flex items-center">
                                        <MdTrendingDown className="mr-0.5" /> {data.kpi.visitors_today.change_pct}%
                                    </span>
                                )}
                                <span className="text-gray-400 font-normal text-[11px]">vs yesterday</span>
                            </div>
                        </div>

                        {/* Unique Visitors Today */}
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
                                <span>Unique Visitors Today</span>
                                <span className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-bold">Unique</span>
                            </div>
                            <div className="text-2xl font-black text-gray-800">
                                {formatNumber(data.kpi.unique_visitors_today.value)}
                            </div>
                            <div className="mt-2 flex items-center gap-1 text-xs font-semibold">
                                {data.kpi.unique_visitors_today.is_increase ? (
                                    <span className="text-emerald-600 flex items-center">
                                        <MdTrendingUp className="mr-0.5" /> +{data.kpi.unique_visitors_today.change_pct}%
                                    </span>
                                ) : (
                                    <span className="text-rose-600 flex items-center">
                                        <MdTrendingDown className="mr-0.5" /> {data.kpi.unique_visitors_today.change_pct}%
                                    </span>
                                )}
                                <span className="text-gray-400 font-normal text-[11px]">vs yesterday</span>
                            </div>
                        </div>

                        {/* Page Views Today */}
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
                                <span>Page Views Today</span>
                                <MdVisibility className="w-4 h-4 text-purple-500" />
                            </div>
                            <div className="text-2xl font-black text-gray-800">
                                {formatNumber(data.kpi.page_views_today.value)}
                            </div>
                            <div className="mt-2 flex items-center gap-1 text-xs font-semibold">
                                {data.kpi.page_views_today.is_increase ? (
                                    <span className="text-emerald-600 flex items-center">
                                        <MdTrendingUp className="mr-0.5" /> +{data.kpi.page_views_today.change_pct}%
                                    </span>
                                ) : (
                                    <span className="text-rose-600 flex items-center">
                                        <MdTrendingDown className="mr-0.5" /> {data.kpi.page_views_today.change_pct}%
                                    </span>
                                )}
                                <span className="text-gray-400 font-normal text-[11px]">vs yesterday</span>
                            </div>
                        </div>

                        {/* Visitors This Month */}
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
                                <span>Visitors This Month</span>
                                <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-bold">Month</span>
                            </div>
                            <div className="text-2xl font-black text-gray-800">
                                {formatNumber(data.kpi.visitors_this_month.value)}
                            </div>
                            <div className="mt-2 flex items-center gap-1 text-xs font-semibold">
                                {data.kpi.visitors_this_month.is_increase ? (
                                    <span className="text-emerald-600 flex items-center">
                                        <MdTrendingUp className="mr-0.5" /> +{data.kpi.visitors_this_month.change_pct}%
                                    </span>
                                ) : (
                                    <span className="text-rose-600 flex items-center">
                                        <MdTrendingDown className="mr-0.5" /> {data.kpi.visitors_this_month.change_pct}%
                                    </span>
                                )}
                                <span className="text-gray-400 font-normal text-[11px]">vs last month</span>
                            </div>
                        </div>

                        {/* Page Views This Month */}
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
                                <span>Page Views This Month</span>
                                <MdVisibility className="w-4 h-4 text-emerald-500" />
                            </div>
                            <div className="text-2xl font-black text-gray-800">
                                {formatNumber(data.kpi.page_views_this_month.value)}
                            </div>
                            <div className="mt-2 flex items-center gap-1 text-xs font-semibold">
                                {data.kpi.page_views_this_month.is_increase ? (
                                    <span className="text-emerald-600 flex items-center">
                                        <MdTrendingUp className="mr-0.5" /> +{data.kpi.page_views_this_month.change_pct}%
                                    </span>
                                ) : (
                                    <span className="text-rose-600 flex items-center">
                                        <MdTrendingDown className="mr-0.5" /> {data.kpi.page_views_this_month.change_pct}%
                                    </span>
                                )}
                                <span className="text-gray-400 font-normal text-[11px]">vs last month</span>
                            </div>
                        </div>
                    </div>

                    {/* 2. Visitor Trend Chart */}
                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
                            <div>
                                <h4 className="font-bold text-gray-800 text-base">Visitor Traffic Trend</h4>
                                <p className="text-xs text-gray-500">Daily breakdown of visitors and total page views</p>
                            </div>
                            <div className="flex items-center gap-4 text-xs font-semibold">
                                <span className="flex items-center gap-1.5 text-gray-700">
                                    <span className="w-3 h-3 rounded-sm bg-blue-600 inline-block"></span>
                                    Visitors
                                </span>
                                <span className="flex items-center gap-1.5 text-gray-700">
                                    <span className="w-3 h-3 rounded-sm bg-sky-400 inline-block"></span>
                                    Page Views
                                </span>
                            </div>
                        </div>

                        {/* Interactive Bars Visualization */}
                        <div className="h-52 w-full flex items-end gap-2 sm:gap-3 pt-6 pb-2 border-b border-gray-100">
                            {data.trend && data.trend.length > 0 ? (
                                data.trend.map((point, idx) => {
                                    const vHeight = Math.max(8, (point.visitors / maxTrendVal) * 100);
                                    const pvHeight = Math.max(8, (point.page_views / maxTrendVal) * 100);

                                    return (
                                        <div key={idx} className="flex-1 flex flex-col items-center h-full justify-end group relative">
                                            {/* Hover Tooltip */}
                                            <div className="absolute -top-12 z-20 hidden group-hover:flex flex-col items-center bg-gray-900 text-white text-[10px] py-1 px-2 rounded shadow-lg whitespace-nowrap pointer-events-none">
                                                <span className="font-bold">{point.date || point.label}</span>
                                                <span>Visitors: {formatNumber(point.visitors)}</span>
                                                <span>Views: {formatNumber(point.page_views)}</span>
                                            </div>

                                            <div className="w-full flex items-end justify-center gap-1 h-full">
                                                <div 
                                                    style={{ height: `${vHeight}%` }} 
                                                    className="w-2.5 sm:w-3.5 bg-blue-600 rounded-t-sm transition-all duration-300 group-hover:bg-blue-700"
                                                ></div>
                                                <div 
                                                    style={{ height: `${pvHeight}%` }} 
                                                    className="w-2.5 sm:w-3.5 bg-sky-400 rounded-t-sm transition-all duration-300 group-hover:bg-sky-500"
                                                ></div>
                                            </div>
                                            <span className="text-[10px] text-gray-400 mt-2 truncate w-full text-center">
                                                {point.date ? point.date.split(' ')[0] : point.label}
                                            </span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 italic text-sm">
                                    No trend data available for this range
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 3, 4, 5, 6: Breakdown Grids */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* 3. Top Countries */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-4 border-b pb-3">
                                    <h4 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                                        <MdPublic className="text-blue-600 text-lg" />
                                        Top Countries
                                    </h4>
                                    <button 
                                        onClick={onViewDetailed}
                                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                    >
                                        View All <MdArrowForward className="w-3 h-3" />
                                    </button>
                                </div>

                                <div className="space-y-3">
                                    {data.top_countries && data.top_countries.length > 0 ? (
                                        data.top_countries.map((c, idx) => (
                                            <div key={idx} className="space-y-1">
                                                <div className="flex justify-between text-xs">
                                                    <span className="font-medium text-gray-700">{c.country}</span>
                                                    <span className="text-gray-500 font-semibold">
                                                        {formatNumber(c.visitors)} ({c.percentage}%)
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                                    <div 
                                                        className="bg-blue-600 h-full rounded-full transition-all duration-500" 
                                                        style={{ width: `${c.percentage}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-xs text-gray-400 italic py-4 text-center">No country data</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 4. Top Pages / Articles */}
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-4 border-b pb-3">
                                <h4 className="font-bold text-gray-800 text-sm">
                                    Top Pages / Articles
                                </h4>
                                <span className="text-[11px] text-gray-400">By page views</span>
                            </div>

                            <div className="space-y-2.5">
                                {data.top_pages && data.top_pages.length > 0 ? (
                                    data.top_pages.map((p, idx) => (
                                        <div key={idx} className="p-2 bg-gray-50 rounded-lg hover:bg-blue-50/50 transition-colors flex items-center justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-semibold text-gray-800 truncate" title={p.title || p.path}>
                                                    {p.title || p.path}
                                                </p>
                                                <p className="text-[11px] text-gray-400 truncate">
                                                    {p.path}
                                                </p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <span className="text-xs font-bold text-gray-800 block">
                                                    {formatNumber(p.views)} <span className="text-[10px] font-normal text-gray-500">views</span>
                                                </span>
                                                <span className="text-[10px] text-blue-600">
                                                    {formatNumber(p.visitors)} visitors
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-xs text-gray-400 italic py-4 text-center">No page views recorded</p>
                                )}
                            </div>
                        </div>

                        {/* 5 & 6. Traffic Sources & Device Breakdown */}
                        <div className="space-y-6">
                            {/* Traffic Sources */}
                            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between mb-3 border-b pb-2.5">
                                    <h4 className="font-bold text-gray-800 text-sm">
                                        Traffic Sources
                                    </h4>
                                    <span className="text-[11px] text-gray-400">Share</span>
                                </div>

                                <div className="space-y-2">
                                    {data.traffic_sources && data.traffic_sources.map((ts, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-xs">
                                            <div className="flex items-center gap-2">
                                                {getSourceIcon(ts.source)}
                                                <span className="text-gray-700 truncate max-w-[130px]">{ts.source}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-gray-800">{ts.percentage}%</span>
                                                <span className="text-gray-400 text-[10px]">({formatNumber(ts.visitors)})</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Device Breakdown */}
                            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between mb-3 border-b pb-2.5">
                                    <h4 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                                        <MdDevices className="text-gray-600" />
                                        Device Breakdown
                                    </h4>
                                </div>

                                <div className="grid grid-cols-3 gap-2 text-center">
                                    <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                                        <MdSmartphone className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                                        <span className="text-xs text-gray-500 block">Mobile</span>
                                        <strong className="text-sm font-black text-gray-800">
                                            {data.devices?.Mobile?.percentage || 0}%
                                        </strong>
                                    </div>

                                    <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                                        <MdComputer className="w-5 h-5 mx-auto text-indigo-600 mb-1" />
                                        <span className="text-xs text-gray-500 block">Desktop</span>
                                        <strong className="text-sm font-black text-gray-800">
                                            {data.devices?.Desktop?.percentage || 0}%
                                        </strong>
                                    </div>

                                    <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                                        <MdTabletMac className="w-5 h-5 mx-auto text-purple-600 mb-1" />
                                        <span className="text-xs text-gray-500 block">Tablet</span>
                                        <strong className="text-sm font-black text-gray-800">
                                            {data.devices?.Tablet?.percentage || 0}%
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 9. Detailed Analytics Action Button */}
                    <div className="flex justify-center pt-2">
                        <button
                            onClick={onViewDetailed}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center gap-2 text-sm"
                        >
                            View Detailed Visitor Statistics
                            <MdArrowForward className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VisitorAnalyticsWidget;
